import { useState } from 'react';
import { useParams, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
} from '@wegroup/design-system';
import AdvisoryAppShell from '../../../../flows/src/components/ui/advisory-flow/components/app-shell/AdvisoryAppShell';
import AdvisoryFlowMainContainer from '../../../../flows/src/components/ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import QuestionGroupAssistantQuestion from '../../../../flows/src/components/ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import ActionButtons from '../../../../flows/src/components/flows-configure/shared/ActionButtons';
import YellowhiveSalesConversationProductSelectionOption, {
  YellowhiveSalesConversationProductOption,
} from './YellowhiveSalesConversationProductSelectionOption';
import useUuid from '../../../../common/src/hooks/useUuid';
import useStartYellowhiveOfferFlow from '../../../../flows/src/hooks/flows-configure/useStartYellowhiveOfferFlow';
import { DisplayInputComponentAccordion } from '../../../../flows/src/components/input/advisory-flow-inputs/display/DisplayInputComponent';

export const CYBER_CLEAR_PRODUCT_ID = 'c69c9851-dc3d-47ea-912d-c11b8fa03323';
export const CYBER_START_PRODUCT_ID = '37383e51-93b4-4bd7-9765-3f0e73783043';
export const MAATWERK_PRODUCT_ID = 'maatwerk-product-id';

const PRODUCT_OPTIONS: YellowhiveSalesConversationProductOption[] = [
  {
    id: CYBER_START_PRODUCT_ID,
    titleKey: 'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_START_TITLE',
    subtitleKey: 'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_START_SUBTITLE',
    featureKeys: [
      'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_START_FEATURE_1',
      'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_START_FEATURE_2',
      'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_START_FEATURE_3',
    ],
  },
  {
    id: CYBER_CLEAR_PRODUCT_ID,
    titleKey: 'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_CLEAR_TITLE',
    subtitleKey: 'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_CLEAR_SUBTITLE',
    featureKeys: [
      'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_CLEAR_FEATURE_1',
      'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_CLEAR_FEATURE_2',
      'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_CYBER_CLEAR_FEATURE_3',
    ],
  },
  {
    id: MAATWERK_PRODUCT_ID,
    titleKey: 'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_MAATWERK_TITLE',
    subtitleKey: 'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_MAATWERK_SUBTITLE',
    featureKeys: [
      'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_MAATWERK_FEATURE_1',
      'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_MAATWERK_FEATURE_2',
      'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_MAATWERK_FEATURE_3',
    ],
  },
];

const YellowhiveSalesConversationProductSelection = () => {
  const { t } = useTranslation();
  const { flowId } = useParams<{ flowId: string }>();
  const location = useLocation();
  const uuid = useUuid();
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMaatwerkModalOpen, setIsMaatwerkModalOpen] = useState(false);
  const startFlow = useStartYellowhiveOfferFlow();

  const handleSelect = (productId: string) => {
    if (productId === selectedProductId) {
      // Deselect when clicking again on the already-selected card
      setSelectedProductId('');
    } else {
      setSelectedProductId(productId);
    }
  };

  const handleSubmit = async () => {
    if (!selectedProductId) return;

    // If Maatwerk is selected, open modal instead of starting flow
    if (selectedProductId === MAATWERK_PRODUCT_ID) {
      setIsMaatwerkModalOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      await startFlow({
        flowId: flowId || '',
        uuid,
        inputUrl: location.pathname,
        selectedYellowhiveProductId: selectedProductId,
      });
      // Flow initialization will handle navigation via pushUserToFlow
    } catch (error) {
      console.error('Error starting flow:', error);
      setIsLoading(false);
    }
  };

  return (
    <AdvisoryAppShell>
      <AdvisoryFlowMainContainer>
        <QuestionGroupAssistantQuestion
          question={t('SALES-CONVERSATION_YELLOWHIVE_PRODUCT_SELECTION_TITLE')}
        />
        <Box
          // from QuestionGroupBody
          flexGrow={1}
          py={{ base: '2', tabletS: '4', laptop: '8' }}
          pb={{ base: '4', laptop: '8' }}
          px={{ base: '3', laptop: '8' }}
        >
          <Grid
            templateColumns={{
              base: '1fr',
              tablet: 'repeat(2, 1fr)',
              laptop: 'repeat(3, 1fr)',
            }}
            gridGap="2.4rem"
            width="100%"
            maxW="1200px"
            mx="auto"
            justifyContent="center"
            alignItems="stretch"
          >
            {PRODUCT_OPTIONS.map((option) => (
              <YellowhiveSalesConversationProductSelectionOption
                key={option.id}
                option={option}
                isSelected={selectedProductId === option.id}
                onSelect={() => handleSelect(option.id)}
              />
            ))}
          </Grid>
          <Box mt={8} width="100%" maxW="1200px" mx="auto">
            <DisplayInputComponentAccordion
              title={t(
                'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_SELECTION_DISPLAY_TITLE',
              )}
              subtitle={t(
                'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_SELECTION_DISPLAY_DESCRIPTION',
              )}
            />
          </Box>
        </Box>
        <ActionButtons
          onContinue={handleSubmit}
          disabledContinue={!selectedProductId}
          isLoading={isLoading}
        />
      </AdvisoryFlowMainContainer>
      <Modal
        isOpen={isMaatwerkModalOpen}
        onClose={() => setIsMaatwerkModalOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('SALES-CONVERSATION_YELLOWHIVE_PRODUCT_MAATWERK_MODAL_TITLE')}
          </ModalHeader>
          <ModalBody>
            <VStack spacing="4" align="stretch">
              <Text>
                {t(
                  'SALES-CONVERSATION_YELLOWHIVE_PRODUCT_MAATWERK_MODAL_DESCRIPTION',
                )}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsMaatwerkModalOpen(false)}>
              {t('SALES-CONVERSATION_YELLOWHIVE_PRODUCT_MAATWERK_MODAL_C2A')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdvisoryAppShell>
  );
};

export default YellowhiveSalesConversationProductSelection;
